/**
 * Post-ID&V Medium Security routing (SCRN-03 ID&V routing / CCRS-3280, #592).
 *
 * After a customer is authenticated (ID&V online-card + PAN, the IVR fast-path,
 * or the online Customer-Search journey) the agent proceeds to a Medium Security
 * screen. WHICH Medium Security screen depends on the customer's segment:
 *
 *   - a recognised BUSINESS customer (a Business24 online card) → the **Business
 *     24 Medium Security** journey (SCRN-05, `/security/bus24-medium`);
 *   - everyone else → the generic/EDS **Medium Security** journey (SCRN-03,
 *     `/security/medium`), which is unchanged.
 *
 * The business signal is derived SERVER-SIDE: the BFF sources the customer's
 * segment from the gateway `customer/identification` response (`CustDescription`
 * → normalised to `Segment`), and maps it to a `registrationType`
 * (`BUS24` → `BUSINESS24`). Both values are placed on the customer context by
 * the identifying screen; this helper reads only those server-verified fields —
 * never a client-declared discriminator (a business-vs-personal decision must
 * not be spoofable from the client).
 *
 * Before #592 all of these entry points navigated unconditionally to
 * `/security/medium`, so a Business24 customer was mis-routed to the personal
 * EDS Medium Security screen instead of the Business Medium Security journey.
 */

/** SCRN-03 — generic / EDS Medium Security (the default). */
export const MEDIUM_SECURITY_ROUTE = '/security/medium';
/** SCRN-05 — Business 24 Medium Security (CCF-23). */
export const BUS24_MEDIUM_SECURITY_ROUTE = '/security/bus24-medium';

/**
 * The customer fields this helper reads. Both are populated server-side from the
 * gateway segment; either one being the business marker is sufficient (the ID&V
 * journey carries `registrationType`, the Customer-Search journey carries only
 * `segment`).
 */
export interface SecurityRoutingCustomer {
  /** Gateway customer segment (`EDS` | `VISA` | `BUS24`). */
  readonly segment?: string;
  /** Registration type derived server-side from the segment (`BUS24` → `BUSINESS24`). */
  readonly registrationType?: string;
}

/**
 * Whether the (server-verified) customer context marks a Business24 / business
 * customer. Matches on the raw gateway segment (`BUS24`) OR the BFF-derived
 * registration type (`BUSINESS24`) — case-insensitively, defensively.
 */
export function isBusinessCustomer(
  customer: SecurityRoutingCustomer | undefined,
): boolean {
  if (!customer) return false;
  const segment = customer.segment?.trim().toUpperCase();
  const registrationType = customer.registrationType?.trim().toUpperCase();
  return segment === 'BUS24' || registrationType === 'BUSINESS24';
}

/**
 * The Medium Security route for an authenticated customer: the Business 24
 * Medium Security journey (SCRN-05) for a recognised business customer, else the
 * generic/EDS Medium Security journey (SCRN-03). Fail-safe: an unknown/absent
 * segment routes to the generic Medium Security screen (the prior behaviour), so
 * the personal journey is never mis-routed to Business.
 */
export function mediumSecurityRouteFor(
  customer: SecurityRoutingCustomer | undefined,
): string {
  return isBusinessCustomer(customer)
    ? BUS24_MEDIUM_SECURITY_ROUTE
    : MEDIUM_SECURITY_ROUTE;
}
